import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { text, type Locale } from '../shared/content';
import type { AdAssetMetadata } from '../server/ad-assets';
import { ApiError, api } from './api';

const MAX_BYTES = 2 * 1024 * 1024;
const copy = {
  choose: text('Choose advertisement image', '选择广告图片', '選擇廣告圖片', 'Elegir imagen publicitaria'),
  limits: text(
    'Static PNG, JPEG or WebP · up to2MiB, 2048px per side and4MP. Images are converted to WebP and metadata is removed.',
    '静态PNG、JPEG或WebP；最大2MiB、单边2048像素、总计400万像素。上传后转为WebP并移除元数据。',
    '靜態PNG、JPEG或WebP；最大2MiB、單邊2048像素、總計400萬像素。上傳後轉為WebP並移除中繼資料。',
    'PNG, JPEG o WebP estático · hasta2MiB, 2048px por lado y4MP. Se convierte a WebP y se eliminan metadatos.',
  ),
  private: text(
    'Private operator preview · uploading does not publish an advertisement. Add alternative text and submit the creative for review.',
    '运营私有预览：上传不会发布广告。请填写替代文本并提交素材审核。',
    '營運私有預覽：上傳不會發布廣告。請填寫替代文字並提交素材審核。',
    'Vista privada del equipo: subir no publica un anuncio. Añade texto alternativo y envía la creatividad a revisión.',
  ),
  busy: text('Processing image…', '正在处理图片…', '正在處理圖片…', 'Procesando imagen…'),
  saved: text('Private image uploaded', '私有图片上传成功', '私有圖片上傳成功', 'Imagen privada subida'),
  preview: text('Private advertisement preview', '广告私有预览', '廣告私有預覽', 'Vista privada del anuncio'),
  invalid: text(
    'Choose a valid static PNG, JPEG or WebP within the image limits.',
    '请选择符合大小和尺寸限制的静态PNG、JPEG或WebP。',
    '請選擇符合大小與尺寸限制的靜態PNG、JPEG或WebP。',
    'Elige un PNG, JPEG o WebP estático dentro de los límites.',
  ),
  retry: text(
    'Image processing is busy. Try again shortly.',
    '图片处理繁忙，请稍后重试。',
    '圖片處理繁忙，請稍後重試。',
    'El procesamiento está ocupado. Inténtalo en breve.',
  ),
  quota: text(
    'Image storage is full. Contact the platform operator.',
    '图片存储额度已满，请联系平台管理员。',
    '圖片儲存額度已滿，請聯絡平台管理員。',
    'El almacenamiento está lleno. Contacta con el operador.',
  ),
  forbidden: text(
    'Advertisement operator access is required.',
    '需要广告运营权限。',
    '需要廣告營運權限。',
    'Se requiere permiso de publicidad.',
  ),
  failed: text(
    'The image could not be uploaded. Try again or contact the operator.',
    '图片上传失败，请重试或联系平台管理员。',
    '圖片上傳失敗，請重試或聯絡平台管理員。',
    'No se pudo subir la imagen. Reintenta o contacta con el operador.',
  ),
};
function base64(bytes: Uint8Array) {
  let value = '';
  for (let offset = 0; offset < bytes.length; offset += 32768)
    value += String.fromCharCode(...bytes.subarray(offset, offset + 32768));
  return btoa(value);
}
export default function AdAssetUpload({
  locale,
  selectedAssetId,
  selectionKey,
  onUploaded,
  onMessage,
}: {
  locale: Locale;
  selectedAssetId: string | null;
  selectionKey: string;
  onUploaded: (asset: AdAssetMetadata) => void | boolean;
  onMessage?: (message: string, error?: boolean) => void;
}) {
  const selected = selectedAssetId || '';
  const latest = useRef({ selectionKey, selected });
  latest.current = { selectionKey, selected };
  const active = useRef<
    | {
        id: number;
        selectionKey: string;
        selected: string;
        controller: AbortController;
        timeout: ReturnType<typeof setTimeout>;
        input: HTMLInputElement;
      }
    | undefined
  >(undefined);
  const sequence = useRef(0);
  const [pending, setPending] = useState<number>();
  const [uploadedAsset, setUploadedAsset] = useState<{ selectionKey: string; asset: AdAssetMetadata }>();
  const [message, setMessage] = useState<{
    selectionKey: string;
    selected: string;
    assetId?: string;
    key: keyof typeof copy;
  }>();
  const validId = (id: string) =>
    /^[a-f0-9]{8}-[a-f0-9]{4}-[1-8][a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i.test(id);
  const previewId = validId(selected) ? selected.toLowerCase() : '';
  const asset =
    uploadedAsset?.selectionKey === selectionKey && uploadedAsset.asset.id.toLowerCase() === previewId
      ? uploadedAsset.asset
      : undefined;
  const busy =
    pending !== undefined &&
    active.current?.id === pending &&
    active.current.selectionKey === selectionKey &&
    active.current.selected === selected;
  const showMessage =
    message?.selectionKey === selectionKey &&
    (message.assetId ? message.assetId.toLowerCase() === previewId : message.selected === selected);
  useEffect(() => {
    return () => {
      const request = active.current;
      if (request?.selectionKey !== selectionKey || request.selected !== selected) return;
      active.current = undefined;
      clearTimeout(request.timeout);
      request.controller.abort();
      request.input.value = '';
    };
  }, [selectionKey, selected]);
  const t = (key: keyof typeof copy) => copy[key][locale];
  const choose = async (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget,
      file = input.files?.[0];
    if (!file || busy) return;
    if (active.current) {
      clearTimeout(active.current.timeout);
      active.current.controller.abort();
    }
    const controller = new AbortController();
    const request = {
      id: ++sequence.current,
      selectionKey,
      selected,
      controller,
      timeout: setTimeout(() => controller.abort(), 20000),
      input,
    };
    active.current = request;
    setPending(request.id);
    setMessage(undefined);
    const current = () =>
      active.current === request &&
      latest.current.selectionKey === request.selectionKey &&
      latest.current.selected === request.selected;
    try {
      if (file.size > MAX_BYTES || !['image/png', 'image/jpeg', 'image/webp'].includes(file.type))
        throw new ApiError('AD_ASSET_IMAGE', t('invalid'), 400);
      const bytes = await file.arrayBuffer();
      if (!current()) return;
      const response = await api('/admin/ad-assets', {
        method: 'POST',
        body: JSON.stringify({ base64: base64(new Uint8Array(bytes)) }),
        signal: controller.signal,
      });
      if (!current()) return;
      const uploaded = (response.asset || response) as AdAssetMetadata;
      if (
        !validId(uploaded.id) ||
        uploaded.mime !== 'image/webp' ||
        !Number.isSafeInteger(uploaded.bytes) ||
        uploaded.bytes > MAX_BYTES
      )
        throw new Error('Invalid asset response.');
      const safe = { ...uploaded, previewPath: `/api/admin/ad-assets/${uploaded.id}` };
      // The parent can reject a result after a synchronous editor change that
      // has not rendered yet. Never write A's image into B's draft or toast it.
      if (onUploaded(safe) === false) return;
      setUploadedAsset({ selectionKey, asset: safe });
      setMessage({ selectionKey, selected, assetId: safe.id, key: 'saved' });
      onMessage?.(t('saved'));
    } catch (error) {
      if (!current()) return;
      const code = error instanceof ApiError ? error.code : '';
      const key =
        code === 'ADMIN'
          ? 'forbidden'
          : code === 'AD_ASSET_IMAGE' || code === 'VALIDATION'
            ? 'invalid'
            : code === 'AD_ASSET_BUSY'
              ? 'retry'
              : code === 'AD_ASSET_QUOTA'
                ? 'quota'
                : 'failed';
      setMessage({ selectionKey, selected, key });
      onMessage?.(t(key), true);
    } finally {
      clearTimeout(request.timeout);
      if (active.current === request) {
        active.current = undefined;
        input.value = '';
        setPending(undefined);
      }
    }
  };
  return (
    <section className="ad-asset-upload">
      <label>
        {t('choose')}
        <input
          aria-label={t('choose')}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          disabled={busy}
          onChange={choose}
        />
      </label>
      <p className="muted small">{t('limits')}</p>
      <p className="muted small">{t('private')}</p>
      {(busy || showMessage) && <p role="status">{busy ? t('busy') : t(message!.key)}</p>}
      {previewId && (
        <figure>
          <img
            src={`/api/admin/ad-assets/${previewId}`}
            alt={t('preview')}
            style={{ maxWidth: '100%', maxHeight: 220, objectFit: 'contain' }}
          />
          {asset && (
            <figcaption>
              {asset.width} × {asset.height} · WebP · {Math.ceil(asset.bytes / 1024)}KiB
            </figcaption>
          )}
        </figure>
      )}
    </section>
  );
}
